import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useAviso } from '../../contexts/AvisoContext';
import { useAuth } from '../../contexts/AuthContext';
import AvisoItem from './AvisoItem';
import NovoAvisoForm from './NovoAvisoForm';
import { Aviso } from '../../types/avisos';
import ConfirmDialog from '../common/ConfirmDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`aviso-tabpanel-${index}`}
      aria-labelledby={`aviso-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ListaAvisos: React.FC = () => {
  const { 
    avisos, 
    avisosFixados,
    avisosNaoLidos,
    carregando, 
    erro, 
    obterAvisos,
    criarAviso,
    atualizarAviso,
    excluirAviso,
    marcarComoLido,
    fixarAviso,
    desfixarAviso
  } = useAviso();
  
  const { usuario } = useAuth();
  
  const [tabAtual, setTabAtual] = useState(0);
  const [formAvisoAberto, setFormAvisoAberto] = useState(false);
  const [avisoParaEditar, setAvisoParaEditar] = useState<Aviso | undefined>(undefined);
  const [avisoParaExcluir, setAvisoParaExcluir] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Verificar se o usuário é admin (apenas admins podem publicar avisos)
  const podePublicarAvisos = usuario?.tipo === 'admin';
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabAtual(newValue);
  };
  
  const handleNovoAviso = () => {
    setAvisoParaEditar(undefined);
    setFormAvisoAberto(true);
  };
  
  const handleEditarAviso = (id: string) => {
    const aviso = avisos.find(a => a.id === id);
    if (aviso) {
      setAvisoParaEditar(aviso);
      setFormAvisoAberto(true);
    }
  };
  
  const handleExcluirAviso = (id: string) => {
    setAvisoParaExcluir(id);
    setConfirmDialogOpen(true);
  };
  
  const confirmarExclusao = async () => {
    if (avisoParaExcluir) {
      await excluirAviso(avisoParaExcluir);
      setAvisoParaExcluir(null);
    }
    setConfirmDialogOpen(false);
  };
  
  const handleSubmitAviso = async (aviso: Omit<Aviso, 'id' | 'data_criacao'>) => {
    if (avisoParaEditar) {
      await atualizarAviso(avisoParaEditar.id, aviso);
    } else {
      await criarAviso(aviso);
    }
    await obterAvisos();
  };
  
  const handleAvisoClick = async (id: string) => {
    // Marcar como lido ao clicar
    const aviso = avisos.find(a => a.id === id);
    if (aviso && !aviso.lido) {
      try {
        // Marcar o aviso como lido e aguardar a conclusão
        await marcarComoLido(id);
        
        // Forçar uma atualização da lista de avisos para garantir que o contador seja atualizado
        await obterAvisos();
      } catch (error) {
        console.error('Erro ao marcar aviso como lido:', error);
      }
    }
    
    // Aqui poderia abrir um modal com detalhes do aviso
    // Por enquanto apenas marca como lido
  };

  // Abre automaticamente o aviso vindo de um link de email (?avisoId=...):
  // garante a aba "Todos os Avisos" (onde ele certamente aparece), rola até
  // o card correspondente e aplica um destaque temporário.
  useEffect(() => {
    if (carregando) return;
    const params = new URLSearchParams(window.location.search);
    const avisoId = params.get('avisoId');
    if (!avisoId) return;

    setTabAtual(0);

    const elemento = document.getElementById(`aviso-${avisoId}`);
    if (elemento) {
      elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Destaque via outline (não via cor de fundo) para não depender do
      // tema atual — uma cor de fundo clara fixa fica ilegível no modo
      // escuro, pois o texto do card herda a cor clara do tema.
      elemento.style.outline = '3px solid #e10600';
      elemento.style.outlineOffset = '2px';
      setTimeout(() => {
        elemento.style.outline = '';
        elemento.style.outlineOffset = '';
      }, 2500);
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('avisoId');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregando]);

  // Filtrar avisos não fixados (para exibir abaixo dos fixados)
  const avisosNaoFixados = avisos.filter(aviso => !aviso.fixado);
  
  // Filtrar avisos não lidos para a aba de não lidos
  const avisosApenasNaoLidos = avisos.filter(aviso => !aviso.lido);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Avisos
        </Typography>
        
        {podePublicarAvisos && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNovoAviso}
          >
            Novo Aviso
          </Button>
        )}
      </Box>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabAtual}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            label="Todos os Avisos" 
            icon={<NotificationsIcon />} 
            iconPosition="start"
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <span>Não Lidos</span>
                {avisosNaoLidos > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-2">
                    {avisosNaoLidos > 99 ? '99+' : avisosNaoLidos}
                  </span>
                )}
              </Box>
            }
          />
          <Tab 
            label="Fixados" 
            icon={<PushPinIcon />} 
            iconPosition="start"
            disabled={avisosFixados.length === 0}
          />
        </Tabs>
      </Paper>
      
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}
      
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabAtual} index={0}>
            {avisosFixados.length > 0 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" component="h2" gutterBottom>
                    Avisos Fixados
                  </Typography>
                  
                  {avisosFixados.map((aviso) => (
                    <AvisoItem
                      key={aviso.id}
                      aviso={aviso}
                      onMarcarLido={marcarComoLido}
                      onFixar={fixarAviso}
                      onDesafixar={desfixarAviso}
                      onEditar={podePublicarAvisos ? handleEditarAviso : undefined}
                      onExcluir={podePublicarAvisos ? handleExcluirAviso : undefined}
                      onClick={handleAvisoClick}
                    />
                  ))}
                </Box>
                
                <Divider sx={{ my: 3 }} />
              </>
            )}
            
            {avisosNaoFixados.length > 0 ? (
              <>
                <Typography variant="subtitle1" component="h2" gutterBottom>
                  Todos os Avisos
                </Typography>
                
                {avisosNaoFixados.map((aviso) => (
                  <AvisoItem
                    key={aviso.id}
                    aviso={aviso}
                    onMarcarLido={marcarComoLido}
                    onFixar={fixarAviso}
                    onDesafixar={desfixarAviso}
                    onEditar={podePublicarAvisos ? handleEditarAviso : undefined}
                    onExcluir={podePublicarAvisos ? handleExcluirAviso : undefined}
                    onClick={handleAvisoClick}
                  />
                ))}
              </>
            ) : (
              avisosFixados.length === 0 && (
                <Alert severity="info">
                  Não há avisos disponíveis no momento.
                </Alert>
              )
            )}
          </TabPanel>
          
          <TabPanel value={tabAtual} index={1}>
            {avisosApenasNaoLidos.length > 0 ? (
              avisosApenasNaoLidos.map((aviso) => (
                <AvisoItem
                  key={aviso.id}
                  aviso={aviso}
                  onMarcarLido={marcarComoLido}
                  onFixar={fixarAviso}
                  onDesafixar={desfixarAviso}
                  onEditar={podePublicarAvisos ? handleEditarAviso : undefined}
                  onExcluir={podePublicarAvisos ? handleExcluirAviso : undefined}
                  onClick={handleAvisoClick}
                />
              ))
            ) : (
              <Alert severity="success">
                Você não tem avisos não lidos.
              </Alert>
            )}
          </TabPanel>
          
          <TabPanel value={tabAtual} index={2}>
            {avisosFixados.length > 0 ? (
              avisosFixados.map((aviso) => (
                <AvisoItem
                  key={aviso.id}
                  aviso={aviso}
                  onMarcarLido={marcarComoLido}
                  onFixar={fixarAviso}
                  onDesafixar={desfixarAviso}
                  onEditar={podePublicarAvisos ? handleEditarAviso : undefined}
                  onExcluir={podePublicarAvisos ? handleExcluirAviso : undefined}
                  onClick={handleAvisoClick}
                />
              ))
            ) : (
              <Alert severity="info">
                Não há avisos fixados no momento.
              </Alert>
            )}
          </TabPanel>
        </>
      )}
      
      <NovoAvisoForm
        open={formAvisoAberto}
        onClose={() => setFormAvisoAberto(false)}
        onSubmit={handleSubmitAviso}
        avisoParaEditar={avisoParaEditar}
      />
      
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Excluir Aviso"
        message="Tem certeza que deseja excluir este aviso? Esta ação não pode ser desfeita."
        onConfirm={confirmarExclusao}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setAvisoParaExcluir(null);
        }}
      />
    </Box>
  );
};

export default ListaAvisos;
